import React, { useEffect, useId, useRef, useState } from 'react';
import Checkbox from './Checkbox';
import TextLink from './TextLink';
import HighlightedText from './HighlightedText';
import { cn } from './cn';

/** Plain text disclosure; optional checkbox state and handlers belong to the caller. */
export default function ExpandableText({ text, lines = 3, muted = false, checkbox, query }) {
  const [expanded, setExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const textRef = useRef(null);
  const textId = useId();
  const searching = !!query?.trim();
  const showFullText = expanded || searching;
  useEffect(() => {
    if (showFullText) return undefined;
    const element = textRef.current;
    const measure = () => setHasMore(element.scrollHeight > element.clientHeight + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => observer.disconnect();
  }, [text, lines, showFullText]);

  const content = <span id={textId} ref={textRef}
    className={cn('block min-w-0 whitespace-pre-wrap break-words font-instrument text-bun-body leading-relaxed', muted ? 'text-bun-stone' : 'text-bun-slate')}
    style={showFullText ? undefined : { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflow: 'hidden' }}><HighlightedText text={text} query={query} /></span>;
  return <div className="min-w-0">
    {checkbox ? <Checkbox className="w-full items-start [&>span:first-child]:mt-1" checked={checkbox.checked}
      disabled={checkbox.disabled} onChange={checkbox.onChange} aria-label={text} label={content} /> : content}
    {!searching && (hasMore || expanded) && <TextLink as="button" size="meta" className={cn('mt-1', checkbox && 'ml-6')}
      aria-expanded={expanded} aria-controls={textId} aria-describedby={textId}
      onClick={() => setExpanded(value => !value)}>{expanded ? 'Show Less' : 'Show More'}</TextLink>}
  </div>;
}
